import { Badge, Box, Card, Center, Flex, Heading, HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import ApplicationErrorView from '@packages/charka-common-ui/graphql/error/application-error-view';
import { getFragmentData } from '@packages/charka-common-ui/graphql/types';
import { MenuSectionItemFragmentDoc } from '@packages/charka-common-ui/graphql/types/graphql';
import { naturalCompare } from '@packages/charka-common-ui/utils/natural-compare';
import { useTranslate } from '@tolgee/react';
import { capitalize, differenceWith, isEqual, uniqWith } from 'lodash';
import { IoChevronBack } from 'react-icons/io5';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AssignmentSwitchButton from './component/assignment-switch-button';
import CreatePageRule from './component/create-page-rule-dialog';
import PrinterDeviceCard from './component/printer-device-card';
import PrinterLabel from './component/printer-label';
import UnassignedSectionAlert from './component/unassigned-section-alert';
import UpdatePageRule from './component/update-page-rule-dialog';
import { useGetPageRuleData } from './logic/use-get-page-rule-data';

interface SectionData {
  id: string;
  code: string;
  name: string;
}

export default function PrintPageRule() {
  const ruleId = useParams().ruleId ?? '';
  const menuId = useParams().menuId ?? '';

  const navigate = useNavigate();
  const { t } = useTranslate();

  const { data, error, loading } = useGetPageRuleData(ruleId, menuId);

  const [searchParams] = useSearchParams();

  if (loading) {
    return (
      <Center>
        <Spinner color="blue.500" borderWidth="2px" />
      </Center>
    );
  }

  if (error) {
    return <>Error when get print rule data</>;
  }

  const sortedPageData = data?.printRule.pages.slice().sort((a, b) => naturalCompare(a.name, b.name));

  const pageData = sortedPageData?.map((page) => {
    const sortedSectionByCode = page.allowedSections.sort((a, b) =>
      naturalCompare(a.menuSection.code, b.menuSection.code),
    );
    const section = sortedSectionByCode.map((section) => (
      <Badge p={2.5} fontSize={'md'} key={section.menuSection.id}>
        {section.menuSection.code}. {section.menuSection.name}
      </Badge>
    ));

    return (
      <Box key={page.id} bgColor={'white'} px={4} maxW={'5xl'} shadow={'sm'} rounded={'md'}>
        <Card.Root border={'none'}>
          <Card.Header px={0}>
            <HStack justifyContent={'space-between'}>
              <VStack alignItems={'start'} fontSize={'xl'} fontWeight={600}>
                <Text display={'inline-block'}>{capitalize(page.name)}</Text>
                <PrinterLabel printerId={page.printerDeviceId} />
              </VStack>
              <UpdatePageRule id={page.id} />
            </HStack>
          </Card.Header>
          <Card.Body px={0}>
            <Flex wrap={'wrap'} gap={3}>
              {section}
            </Flex>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  });

  const resolvedSection = getFragmentData(MenuSectionItemFragmentDoc, data?.menu.sections);

  // Get all menu section of menu
  const menuSections: SectionData[] =
    resolvedSection?.map((item) => ({
      id: item.id,
      code: item.code,
      name: item.name,
    })) ?? [];

  // Get page rule allowed section
  const allowedSections: SectionData[] =
    data?.printRule.pages.flatMap((page) =>
      page.allowedSections.map((section) => ({
        id: section.menuSection.id,
        code: section.menuSection.code,
        name: section.menuSection.name,
      })),
    ) ?? [];

  // remove duplicate section
  const uniqueAllowedSections: SectionData[] = uniqWith(allowedSections, isEqual);

  // get not assigned sections
  const notAssignedSection = differenceWith(menuSections, uniqueAllowedSections, isEqual);

  return (
    <VStack gap={4} alignItems={'stretch'}>
      <ApplicationErrorView error={error} />
      <Box
        position={{ base: 'sticky', lg: 'none' }}
        top={{ base: 16, lg: 0 }}
        zIndex={{ base: 10, lg: 0 }}
        bgColor={'white'}
        px={4}
        maxW={'5xl'}
        shadow={'sm'}
        rounded={'md'}
      >
        <VStack align={'start'} py={4} gap={5}>
          <Heading fontSize={'2xl'} fontWeight={600}>
            <HStack>
              <Icon
                onClick={() => {
                  navigate(`../..?service=${searchParams.get('service') ?? ''}`);
                }}
              >
                <IoChevronBack size={30} />
              </Icon>

              <Text>{t('print-rule-header', 'Menu print rule')}</Text>
            </HStack>
          </Heading>
          <Flex flexWrap={'wrap'} gap={3}>
            <CreatePageRule />
            <AssignmentSwitchButton menuPrintRuleId={ruleId} />
          </Flex>
        </VStack>
      </Box>
      <PrinterDeviceCard printRuleId={ruleId} />

      {pageData}

      <Box maxW={'5xl'}>
        <UnassignedSectionAlert menuSections={notAssignedSection} />
      </Box>
    </VStack>
  );
}
